// src/components/ModalSaldoInicial.jsx
import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, InputGroup, Spinner } from "react-bootstrap";

import { getClientes } from "../helpers/rutaClientes";
import { getTransportes } from "../helpers/rutaTransportes";
import { postSaldoInicialComanda } from "../helpers/rutaComandas";

const sortES = (a, b) =>
  (a || "").localeCompare(b || "", "es", { sensitivity: "base" });

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseARS = (s = "") => {
  const clean = String(s).replace(/[^\d,.-]/g, "");
  const normalized = clean.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const todayYYYYMMDD = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export default function ModalSaldoInicial({ show, onClose, onSaved, defaultTipo, defaultEntidadId, defaultFecha }) {
  const [clientes, setClientes] = useState([]);
  const [transportes, setTransportes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    tipo: "CLIENTE", // CLIENTE | TRANSPORTE
    fecha: todayYYYYMMDD(),
    clienteId: "",
    transporteId: "",
    monto: "",
    observaciones: "SALDO INICIAL",
  });

  useEffect(() => {
    if (!show) return;

    setErr("");
    setSaving(false);
    setLoading(true);

  // ✅ Prefill (no pisa si el usuario ya tocó algo)
  setForm((f) => {
    const tipo = defaultTipo || f.tipo;
    const next = { ...f };

    if (defaultFecha) next.fecha = f.fecha || defaultFecha;
    if (defaultTipo) next.tipo = tipo;

    if (defaultEntidadId) {
      if (tipo === "CLIENTE") {
        next.clienteId = f.clienteId || defaultEntidadId;
        next.transporteId = ""; // limpio el otro
      } else {
        next.transporteId = f.transporteId || defaultEntidadId;
        next.clienteId = "";
      }
    }
    return next;
  });

    (async () => {
      try {
        const [cRaw, tRaw] = await Promise.all([getClientes(), getTransportes()]);

        const cList = Array.isArray(cRaw) ? cRaw : cRaw?.clientes || cRaw?.data || [];
        const tList = Array.isArray(tRaw) ? tRaw : tRaw?.transportes || tRaw?.data || [];

        cList.sort((a, b) => sortES(a?.razonsocial || "", b?.razonsocial || ""));
        tList.sort((a, b) => sortES(a?.transporte || "", b?.transporte || ""));

        setClientes(cList);
        setTransportes(tList);
      } catch (e) {
        setErr("No se pudieron cargar listas (clientes/transportes).");
      } finally {
        setLoading(false);
      }
    })();
  }, [show, defaultTipo, defaultEntidadId, defaultFecha]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onChangeTipo = (e) => {
    const tipo = e.target.value;
    setForm((s) => ({
      ...s,
      tipo,
      clienteId: "",
      transporteId: "",
    }));
  };

  const onMoneyBlur = () => {
    setForm((s) => {
      const n = parseARS(s.monto);
      return { ...s, monto: n ? fmtARS(n) : "" };
    });
  };

  const canSave = useMemo(() => {
    const montoN = parseARS(form.monto);
    if (!(montoN > 0)) return false;
    if (!form.fecha) return false;

    if (form.tipo === "CLIENTE" && !form.clienteId) return false;
    if (form.tipo === "TRANSPORTE" && !form.transporteId) return false;

    return true;
  }, [form]);

  const guardar = async () => {
    if (saving) return;

    try {
      setErr("");
      setSaving(true);

      const payload = {
        tipo: form.tipo, // CLIENTE | TRANSPORTE
        fecha: form.fecha,
        monto: Number(parseARS(form.monto).toFixed(2)),
        observaciones: form.observaciones || "SALDO INICIAL",
        ...(form.tipo === "CLIENTE"
          ? { clienteId: form.clienteId }
          : { transporteId: form.transporteId }),
      };

      const r = await postSaldoInicialComanda(payload);
      if (!r?.ok) throw new Error(r?.error || "No se pudo guardar el saldo inicial.");

      onSaved?.(r);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Error guardando saldo inicial.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Saldo Inicial</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {err ? <Alert variant="danger">{err}</Alert> : null}

        {loading ? (
          <div className="py-4 text-center">
            <Spinner /> Cargando listas…
          </div>
        ) : (
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tipo</Form.Label>
                <Form.Select name="tipo" value={form.tipo} onChange={onChangeTipo}>
                  <option value="CLIENTE">Cliente</option>
                  <option value="TRANSPORTE">Transporte</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Fecha</Form.Label>
                <Form.Control name="fecha" value={form.fecha} onChange={onChange} type="date" />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Monto</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    name="monto"
                    value={form.monto}
                    onChange={onChange}
                    onBlur={onMoneyBlur}
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    style={{ fontWeight: 700, textAlign: "right" }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>

            {form.tipo === "CLIENTE" ? (
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select name="clienteId" value={form.clienteId} onChange={onChange}>
                    <option value="">Seleccionar…</option>
                    {clientes.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.razonsocial || c.nombre || "(sin nombre)"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            ) : (
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Transporte</Form.Label>
                  <Form.Select name="transporteId" value={form.transporteId} onChange={onChange}>
                    <option value="">Seleccionar…</option>
                    {transportes.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.transporte || t.nombre || "(sin transporte)"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            )}

            <Col md={12}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  name="observaciones"
                  value={form.observaciones}
                  onChange={onChange}
                  type="text"
                />
              </Form.Group>
            </Col>
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={guardar} disabled={!canSave || saving}>
          {saving ? "Guardando…" : "Guardar saldo inicial"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}